/**
 * Sidebar3D - Menú lateral con herramientas arrastrables
 * Permite reordenar las secciones con drag & drop
 */
import { useState, useEffect, useCallback, useRef } from 'react';
import { logger } from '@/utils/logger';
import {
  ChartLine, TrendUp, Lightning, Calculator, CalendarBlank, Scales,
  ChartPieSlice, ArrowsClockwise, Database, Buildings, CurrencyCircleDollar,
  Briefcase, Newspaper, Bell, Monitor, CaretLeft, CaretRight, Pulse, DotsSixVertical,
  Keyboard, MagnifyingGlass, ClockCounterClockwise, ChartBar, Globe, Table, GridFour,
  Book, Gauge, Sparkle, Fire, Eye, EyeSlash
} from '@phosphor-icons/react';
import { PulseBadge, useRipple } from './PremiumEffects';
import ToolPreviewPopup from './ToolPreviewPopup';
import ExportHistoryPanel from './ExportHistoryPanel';
import { useExportHistory } from '@/contexts/ExportHistoryContext';

import { WINDOWS_CONFIG } from './WindowManager';

// Función para verificar si el usuario tiene acceso a una herramienta
const hasAccessToTool = (toolId, userPlan) => {
  const windowConfig = WINDOWS_CONFIG.find(w => w.id === toolId);
  if (!windowConfig || !windowConfig.requiredPlan) return true;
  
  // Incluir nombres nuevos (pro, ultimate) y antiguos (basic, premium)
  const planHierarchy = { free: 0, basic: 1, pro: 1, premium: 2, ultimate: 2 };
  const userLevel = planHierarchy[userPlan] || 0;
  const requiredLevel = planHierarchy[windowConfig.requiredPlan] || 0;
  
  return userLevel >= requiredLevel;
};

// Obtener el plan requerido para una herramienta
const getRequiredPlan = (toolId) => {
  const windowConfig = WINDOWS_CONFIG.find(w => w.id === toolId);
  return windowConfig?.requiredPlan || 'free';
};

// Secciones del menú por defecto - Ordenadas ALFABÉTICAMENTE
// TOTAL: 36 HERRAMIENTAS (algunas fusionadas: Alertas+Notif, Comparadores, Heatmaps, Históricos, Calculadoras)
const DEFAULT_MENU_SECTIONS = [
  { id: 'alerts', label: 'Centro de Alertas', icon: Bell, color: '#ff6b6b', description: 'Alertas de precio + Órdenes condicionadas (SL/TP)', hasCounter: true, group: 'free',
    preview: '🔔 Alertas de precio (Price alert) • Órdenes condicionadas Stop loss / Take profit con notas • Vigilancia 24/7 en el servidor • Email al correo de sesión • Sonido e historial' },
  { id: 'emailAlerts', label: 'Alertas por Email', icon: Bell, color: '#ec4899', description: 'Notificaciones al correo', group: 'pro',
    preview: '📧 Alertas de precio por email • Frecuencia configurable • Test de envío • Funciona sin la app abierta • Resend + Gmail' },
  { id: 'analyzer', label: 'Analizador de Activos', icon: MagnifyingGlass, color: '#f59e0b', description: 'Análisis completo + historial', group: 'analysis',
    preview: '🔍 Velocímetro de posición • Historial de análisis • Export PDF' },
  { id: 'asset360', label: 'Análisis Bursátil 360', icon: Sparkle, color: '#E8C55C', description: 'Todo sobre un activo + veredicto IA + web', group: 'analysis',
    preview: '✨ Puerta de entrada • Valoración (Fair Value) + Salud + Altman Z + Técnico + Fundamentales + Noticias • Veredicto Global IA (Gemini) • Investigación web real con fuentes (Perplexity) • Enlaces a las herramientas profundas' },
  { id: 'proAnalyst', label: 'Analista Pro', icon: Sparkle, color: '#ffd700', description: 'Fair Value + Health Score + IA (Ultimate)', group: 'ultimate',
    preview: '✨ NUEVO • Estilo Investing Pro • Fair Value multi-modelo (DCF, P/E, P/S, EV/EBITDA, P/B) • Health Score 1-5 • Altman Z (riesgo quiebra) • ProTips IA • Solo Ultimate' },  { id: 'ebitdaAnalysis', label: 'Análisis Margen EBITDA', icon: ChartBar, color: '#0ea5e9', description: 'Métricas fundamentales EBITDA', group: 'pro',
    preview: '📊 Margen EBITDA • ROE/ROA • P/E • Deuda/EBITDA • Historial trimestral • Buscar ticker o cartera completa • Solo Ultimate' },
  { id: 'indicadoresPro', label: 'Análisis Técnico Profesional', icon: Gauge, color: '#06b6d4', description: 'RSI, MACD, Bollinger, ADX + Conclusión IA y Proyección', group: 'analysis',
    preview: '📊 RSI • MACD • Bollinger • ADX • Fibonacci • ATR • Estocástico • VWAP • Conclusión Profesional IA • Proyección corto/medio/largo plazo (alcista/bajista) • Glosario de abreviaturas' },
  { id: 'rendimiento', label: 'Análisis de Rendimiento', icon: TrendUp, color: '#00ff6a', description: 'Métricas de performance', group: 'free',
    preview: '📈 Cambios 1D/1S/1M/3M/6M/1A • Sentimiento • Recomendación' },
  { id: 'gapHistory', label: 'Historial de Gaps', icon: TrendUp, color: '#E8C55C', description: 'Huecos de apertura detectados', group: 'free',
    preview: '📊 Huecos de apertura (≥3%) detectados en tus activos • % del hueco ▲/▼ • Apertura vs cierre anterior • Filtro por activo y rango (30/90/365 días)' },
  { id: 'categories', label: 'Análisis de Sectores', icon: ChartPieSlice, color: '#06b6d4', description: 'Sectores y categorías del mercado', group: 'analysis',
    preview: '🏭 Tecnología • Finanzas • Salud • Energía • Índices • Materias Primas • Cripto • ETFs' },
  { id: 'signals', label: 'Análisis Señales Trading', icon: Lightning, color: '#ff9500', description: 'Señales automáticas', group: 'analysis',
    preview: '⚡ Patrones de velas • Señales de compra/venta automáticas' },
  { id: 'backtesting', label: 'Backtesting', icon: Database, color: '#3b82f6', description: 'Simula estrategias', group: 'pro',
    preview: '⏪ Prueba estrategias con datos históricos • Ver resultados' },
  { id: 'marketDatabase', label: 'Base de Datos', icon: Table, color: '#06b6d4', description: 'Tabla Excel de mercado', group: 'analysis',
    preview: '📊 Top 20 activos • Máx/Mín • Subida/Bajada • Recomendación expertos • Múltiples periodos' },
  { id: 'backup', label: 'Backup', icon: Database, color: '#22c55e', description: 'Copias de seguridad', group: 'management',
    preview: '💾 Gestiona backups de tus inversiones • Restaurar datos • Protección automática' },
  { id: 'stockScreener', label: 'Buscador Acciones', icon: MagnifyingGlass, color: '#06b6d4', description: 'Filtros avanzados tipo Investing', group: 'free',
    preview: '🔍 400+ tickers • USA, Europa, Emergentes • Filtros: P/E, Dividendos, Sector, Capitalización • Similar a Investing.com' },
  { id: 'qualityTrend', label: 'Calidad + Tendencia', icon: Fire, color: '#f59e0b', description: 'Empresas rentables en tendencia alcista', group: 'free',
    preview: '⭐ Empresas rentables (margen, ROE, PER, PER fwd, PEG, EBITDA) que además están en tendencia alcista (1M, YTD, cerca de máximos 52s/200s) • Enlazado al Mapa de Calor' },
  { id: 'calculatorCenter', label: 'Calculadoras PRO', icon: Calculator, color: '#00dcff', description: 'Riesgo, posiciones y simulador', group: 'analysis',
    preview: '🧮 Tamaño de posición • Stop Loss • Take Profit • Ratio R/R • Simulador de inversiones • Interés compuesto • DCA • ¡2 en 1!' },
  { id: 'economicCalendar', label: 'Calendario Económico', icon: CalendarBlank, color: '#f59e0b', description: 'Eventos que mueven el mercado', group: 'analysis',
    preview: '📅 FED, BCE, Resultados empresariales • Impacto esperado • Explicaciones para principiantes • Fechas clave' },
  { id: 'fullscreen', label: 'Cartera Detallada', icon: Monitor, color: '#ffd700', description: 'Vista de presentación', group: 'special',
    preview: '🖥️ Vista maximizada ideal para monitores grandes' },
  { id: 'livePortfolio', label: 'Cartera en Vivo', icon: Pulse, color: '#00ff6a', description: 'Valor en tiempo real', group: 'management',
    preview: '📊 Valor total de tu cartera actualizado en tiempo real • Gráfico de evolución' },
  { id: 'heatmapCenter', label: 'Centro de Heatmaps', icon: ChartPieSlice, color: '#a855f7', description: 'Correlación + Cartera + Mercado Finviz', group: 'ultimate',
    preview: '🔥 Análisis de correlación • Heatmap de tu cartera • Mapa Finviz del mercado • 3 herramientas en 1 • ¡Todo en tabs!' },
  { id: 'historicalCenter', label: 'Centro de Históricos', icon: Database, color: '#8b5cf6', description: 'OHLCV + Archivo + Máx/Mín', group: 'analysis',
    preview: '📅 Historial OHLCV • Archivo de cartera diario • ATH/ATL y extremos 52s • Export CSV/PDF • 3 herramientas en 1' },
  { id: 'universalComparator', label: 'Comparador de Activos', icon: Scales, color: '#a855f7', description: 'Acciones, Índices y Cripto', group: 'ultimate',
    preview: '⚖️ Compara 2-4 activos • Acciones | Índices | Cripto • Métricas lado a lado • Gráfico comparativo • EUR/USD • ¡Todo en uno!' },
  { id: 'contabilidad', label: 'Contabilidad', icon: Calculator, color: '#10b981', description: 'Registro de beneficios', group: 'management',
    preview: '📊 Registro diario automático • Lo que llevas de semana, mes, año • Historial por días/semanas/meses/años • Solo Ultimate' },
  { id: 'financialDatabase', label: 'Datos Financieros', icon: Scales, color: '#a855f7', description: 'Estados y ratios', group: 'ultimate',
    preview: '📊 Estados Financieros • Ratios de Valoración (PER, PEG) • Salud Financiera (ROE, Deuda) • Dividendos • Análisis para principiantes' },
  { id: 'dividendTracker', label: 'Centro de Dividendos', icon: CurrencyCircleDollar, color: '#10b981', description: 'Lo que cobras (y cobrarás)', group: 'analysis',
    preview: '💰 Total ya cobrado • Proyección 12 meses • Yield sobre coste • Calendario de próximos pagos • Ranking de activos • Gráfico anual • Todo en € y explicado para principiantes' },
  { id: 'buystrategy', label: 'Estrategia de Compra', icon: Briefcase, color: '#10b981', description: 'DCA, rebalanceo y objetivos', group: 'analysis',
    preview: '💡 DCA automático • Precio objetivo • Rebalanceo • Valoración • Exportar/Imprimir' },
  { id: 'watchlists', label: 'Listas de Seguimiento', icon: MagnifyingGlass, color: '#f59e0b', description: 'Watchlists con precios en vivo', group: 'management',
    preview: '📋 Watchlists personalizadas • Precios en vivo • Notas y precio objetivo • Sectores • Gráfico interactivo • API sincronizada' },
  { id: 'manual', label: 'Manual', icon: Book, color: '#22d3ee', description: 'Guía de usuario', group: 'free',
    preview: '📖 Tutorial paso a paso • Cómo usar la app • Primeros pasos • Tips y trucos • Preguntas frecuentes' },
  { id: 'portfolio', label: 'Mi Portafolio', icon: Briefcase, color: '#a855f7', description: 'Tus inversiones', group: 'management',
    preview: '💼 Gestiona inversiones • Beneficio/Pérdida • Exportar PDF' },
  { id: 'multiChart', label: 'Multi-Gráfico', icon: GridFour, color: '#00dcff', description: '4 activos simultáneos', group: 'analysis',
    preview: '📊 Analiza 4 activos al mismo tiempo • Gráficos independientes • Períodos ajustables • Ideal para comparar' },
  { id: 'news', label: 'Noticias', icon: Newspaper, color: '#00dcff', description: 'Últimas noticias', hasCounter: true, group: 'free',
    preview: '📰 Noticias recientes del activo • Traducidas al español' },
  { id: 'financialGoals', label: 'Objetivos Financieros', icon: TrendUp, color: '#a855f7', description: 'Metas de ahorro e inversión', group: 'management',
    preview: '🎯 Define metas de ahorro • Seguimiento visual • Gráficos de progreso • Múltiples objetivos • Presets: Casa, Jubilación, Vacaciones' },
  { id: 'candlestickPatterns', label: 'Patrones de Velas', icon: ChartBar, color: '#22c55e', description: 'Detecta hammer, engulfing, doji...', group: 'analysis',
    preview: '🕯️ Detección automática de patrones • Hammer • Engulfing • Doji • Morning Star • Tipo: alcista/bajista • Volumen' },
  { id: 'sentimentComparator', label: 'Comparador Sentimiento', icon: Scales, color: '#22d3ee', description: 'Compara sentimiento entre tickers y vs índices', group: 'analysis',
    preview: '⚖️ Compara hasta 6 tickers de tu cartera • Detecta divergencias • Explicación IA • NUEVO: Cartera vs S&P 500/Nasdaq/IBEX...' },
  { id: 'tradingJournal', label: 'Diario de Trading', icon: Book, color: '#f97316', description: 'Registro de operaciones', group: 'management',
    preview: '📔 Registra compras/ventas • Emociones del trader • P&L automático • Filtros y búsqueda • Export CSV/PDF • Estadísticas' },
  { id: 'paperTrading', label: 'Paper Trading', icon: Briefcase, color: '#14b8a6', description: 'Practica sin dinero real', group: 'management',
    preview: '💰 Cartera virtual 10.000€ • Compra/Venta • Precios en vivo • Historial de trades • P&L • Reset' },
];

const Sidebar3D = ({ 
  onSectionChange, 
  newsCount = 0,
  alertsCount = 0,
  onCollapseChange = () => {},
  onShowKeyboardShortcuts = () => {},
  onOpenAlertsPanel = () => {}, // Nuevo: abrir el Centro de Alertas unificado
  initialCollapsed = true, // Nuevo: estado inicial controlado desde App.js
  forceCollapsed = null, // Nuevo: forzar estado desde App.js
  currentUser = null, // Usuario actual para verificar plan
  onShowPricing = () => {} // Callback para mostrar página de pricing
}) => {
  const [isCollapsed, setIsCollapsed] = useState(initialCollapsed);
  const [hoveredItem, setHoveredItem] = useState(null);
  const [isEditMode, setIsEditMode] = useState(false);
  const [menuSections, setMenuSections] = useState([]);
  const [draggedItem, setDraggedItem] = useState(null);
  const [dragOverItem, setDragOverItem] = useState(null);
  const [popupToolId, setPopupToolId] = useState(null);
  const [popupPosition, setPopupPosition] = useState({ x: 0, y: 0 });
  const [popupColor, setPopupColor] = useState('#00dcff');
  // Visibilidad de herramientas: el usuario elige cuáles se ven en el menú.
  const [hiddenTools, setHiddenTools] = useState(() => {
    try { return JSON.parse(localStorage.getItem('kapital_hidden_tools') || '[]'); } catch { return []; }
  });
  const toggleToolHidden = useCallback((id) => {
    setHiddenTools((prev) => {
      const next = prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id];
      try { localStorage.setItem('kapital_hidden_tools', JSON.stringify(next)); } catch { /* noop */ }
      window.dispatchEvent(new CustomEvent('toolsVisibilityChanged', { detail: { hidden: next } }));
      return next;
    });
  }, []);
  const resetHiddenTools = useCallback(() => {
    setHiddenTools([]);
    try { localStorage.setItem('kapital_hidden_tools', '[]'); } catch { /* noop */ }
    window.dispatchEvent(new CustomEvent('toolsVisibilityChanged', { detail: { hidden: [] } }));
  }, []);
  const [isMouseInside, setIsMouseInside] = useState(false);
  // Inicializar isMobile basándose en el ancho de ventana si está disponible
  const [isMobile, setIsMobile] = useState(() => {
    if (typeof window !== 'undefined') {
      return window.innerWidth < 768;
    }
    return false;
  });
  
  // Detectar si es móvil
  useEffect(() => {
    const checkMobile = () => {
      const mobile = window.innerWidth < 768;
      setIsMobile(mobile);
      // Colapsar automáticamente en móvil
      if (mobile && !isCollapsed) {
        setIsCollapsed(true);
        onCollapseChange(true);
      }
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, [isCollapsed, onCollapseChange]);
  const hoverTimeoutRef = useRef(null);
  const sidebarRef = useRef(null);
  const collapseTimeoutRef = useRef(null);
  const createRipple = useRipple();
  
  // Sincronizar con forceCollapsed cuando cambia desde App.js
  useEffect(() => {
    if (forceCollapsed !== null && !isMouseInside) {
      setIsCollapsed(forceCollapsed);
      onCollapseChange(forceCollapsed);
    }
  }, [forceCollapsed, onCollapseChange, isMouseInside]);
  
  // Auto-expansión/colapso en HOVER DESACTIVADA (petición usuario jun 2026):
  // la barra lateral de herramientas ya NO se abre al pasar el ratón; solo se
  // abre/cierra manualmente con el botón de toggle. Mantenemos el tracking de
  // isMouseInside (lo usa la lógica de forceCollapsed) pero sin tocar isCollapsed.
  const handleMouseEnter = useCallback(() => {
    if (isMobile) return; // No en móvil
    setIsMouseInside(true);
    if (collapseTimeoutRef.current) {
      clearTimeout(collapseTimeoutRef.current);
      collapseTimeoutRef.current = null;
    }
    // NO expandir automáticamente.
  }, [isMobile]);
  
  const handleMouseLeave = useCallback(() => {
    if (isMobile) return; // No en móvil
    setIsMouseInside(false);
    // NO auto-colapsar: el estado lo controla el usuario manualmente.
  }, [isMobile]);
  
  // Limpiar timeout al desmontar
  useEffect(() => {
    return () => {
      if (collapseTimeoutRef.current) {
        clearTimeout(collapseTimeoutRef.current);
      }
    };
  }, []);
  
  // Usar contexto global para el historial de exportaciones
  const { 
    isPanelOpen: showExportHistory, 
    isPanelMinimized: historyMinimized,
    openPanel: openExportHistory,
    closePanel: closeExportHistory,
    setIsPanelMinimized: setHistoryMinimized
  } = useExportHistory();

  // Siempre usar orden alfabético (ignorar orden guardado)
  useEffect(() => {
    // Ordenar alfabéticamente por label
    const sortedSections = [...DEFAULT_MENU_SECTIONS].sort((a, b) => 
      a.label.localeCompare(b.label, 'es')
    );
    setMenuSections(sortedSections);
    // Limpiar orden guardado antiguo
    localStorage.removeItem('sidebarMenuOrder');
  }, []);

  // Notificar al padre cuando cambie el estado de colapso
  const handleCollapseToggle = () => {
    const newState = !isCollapsed;
    setIsCollapsed(newState);
    onCollapseChange(newState);
  };
  
  // Popup detallado en HOVER DESACTIVADO (petición usuario jun 2026): al pasar el
  // ratón por la barra NO se abre nada. Las herramientas se abren solo con clic.
  const handleToolHover = useCallback((sectionId, sectionColor, e) => {
    // no-op intencional
  }, []);
  
  const handleToolLeave = useCallback(() => {
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
    }
    setHoveredItem(null);
    // Delay LARGO antes de cerrar - 8 segundos para dar tiempo al usuario
    hoverTimeoutRef.current = setTimeout(() => {
      setPopupToolId(null);
    }, 8000);
  }, []);
  
  // Mantener popup abierto cuando el ratón está sobre él
  const handlePopupMouseEnter = useCallback(() => {
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
      hoverTimeoutRef.current = null;
    }
  }, []);
  
  const handlePopupMouseLeave = useCallback(() => {
    // Dar un pequeño delay antes de cerrar por si el usuario quiere volver
    hoverTimeoutRef.current = setTimeout(() => {
      setPopupToolId(null);
    }, 2000);
  }, []);
  
  // Limpiar timeout al desmontar
  useEffect(() => {
    return () => {
      if (hoverTimeoutRef.current) {
        clearTimeout(hoverTimeoutRef.current);
      }
    };
  }, []);

  // Obtener contador para badges
  const getCounter = (sectionId) => {
    if (sectionId === 'news') return newsCount;
    if (sectionId === 'alerts') return alertsCount;
    return 0;
  };

  // Manejar click en sección
  const handleSectionClick = useCallback((sectionId, e) => {
    if (isEditMode) return; // No abrir ventanas en modo edición
    createRipple(e);
    // Limpiar popup al hacer click
    setPopupToolId(null);
    
    // Si es alertas, abrir el nuevo Centro de Alertas unificado
    if (sectionId === 'alerts') {
      onOpenAlertsPanel();
      return;
    }
    
    onSectionChange(sectionId);
  }, [onSectionChange, onOpenAlertsPanel, createRipple, isEditMode]);

  // Drag & Drop handlers
  const handleDragStart = (e, index) => {
    setDraggedItem(index);
    e.dataTransfer.effectAllowed = 'move';
    // Hacer el elemento semi-transparente
    e.target.style.opacity = '0.5';
  };

  const handleDragEnd = (e) => {
    e.target.style.opacity = '1';
    setDraggedItem(null);
    setDragOverItem(null);
  };

  const handleDragOver = (e, index) => {
    e.preventDefault();
    if (draggedItem === null || draggedItem === index) return;
    setDragOverItem(index);
  };

  const handleDrop = (e, dropIndex) => {
    e.preventDefault();
    // Drag & drop deshabilitado - orden alfabético fijo
    setDraggedItem(null);
    setDragOverItem(null);
  };

  // Reset al orden alfabético
  const resetOrder = () => {
    const sortedSections = [...DEFAULT_MENU_SECTIONS].sort((a, b) => 
      a.label.localeCompare(b.label, 'es')
    );
    setMenuSections(sortedSections);
  };

  return (
    <div 
      ref={sidebarRef}
      className={`fixed left-0 top-0 h-full z-40 transition-all duration-500 ease-out 
        hidden md:block
        ${isCollapsed ? 'md:w-16' : 'md:w-56'}
      `}
      style={{
        background: 'linear-gradient(180deg, rgba(20, 16, 8, 0.98) 0%, rgba(12, 10, 5, 0.99) 100%)',
        borderRight: '2px solid rgba(232, 197, 92, 0.18)',
        boxShadow: '4px 0 30px rgba(0, 0, 0, 0.5), 0 0 20px rgba(232, 197, 92, 0.06)',
      }}
      data-testid="sidebar-3d"
      onMouseEnter={!isMobile ? handleMouseEnter : undefined}
      onMouseLeave={!isMobile ? handleMouseLeave : undefined}
      onTouchStart={(e) => e.stopPropagation()}
    >
      {/* Efecto de borde superior */}
      <div 
        className="absolute top-0 left-0 right-0 h-[2px]"
        style={{ background: 'linear-gradient(90deg, transparent, #e8c55c, transparent)' }}
      />

      {/* Header */}
      <div 
        className="p-3 border-b flex items-center justify-between relative"
        style={{ borderColor: 'rgba(232, 197, 92, 0.18)' }}
      >
        {isCollapsed ? (
          <div className="flex items-center justify-start w-full pl-0.5">
            <img 
              src="/logo-kapital-k.png" 
              alt="K" 
              className="h-10 w-10 object-contain"
              style={{ filter: 'drop-shadow(0 0 8px rgba(255, 215, 0, 0.5))' }}
            />
          </div>
        ) : (
          <div className="flex items-center gap-2">
            <img 
              src="/logo-kapital-k.png" 
              alt="Kapital" 
              className="h-12 w-12 object-contain"
              style={{ filter: 'drop-shadow(0 0 10px rgba(255, 215, 0, 0.6))' }}
            />
            <div className="flex flex-col">
              <span className="text-[10px] text-zinc-400 tracking-wider">ANÁLISIS TÉCNICO</span>
              <span className="text-lg font-bold text-gold-400" style={{ textShadow: '0 0 10px rgba(255, 215, 0, 0.5)', marginTop: '-2px' }}>KAPITAL</span>
            </div>
          </div>
        )}
        {!isCollapsed && (
          <button
            onClick={handleCollapseToggle}
            className="p-1.5 rounded-lg hover:bg-gold-500/10 transition-colors"
            style={{ color: '#e8c55c' }}
          >
            <CaretLeft size={18} />
          </button>
        )}
        {isCollapsed && (
          <button
            onClick={handleCollapseToggle}
            title="Abrir barra de herramientas"
            aria-label="Abrir barra de herramientas"
            data-testid="sidebar-expand-button"
            className="absolute top-1/2 -translate-y-1/2 right-1 z-50 p-1 rounded-md hover:bg-gold-500/15 transition-colors"
            style={{ color: '#e8c55c' }}
          >
            <CaretRight size={16} weight="bold" />
          </button>
        )}
      </div>

      {/* Botón de editar orden */}
      {!isCollapsed && (
        <div className="px-3 py-2 flex gap-2">
          <button
            onClick={() => setIsEditMode(!isEditMode)}
            className={`flex-1 text-xs py-1.5 px-2 rounded-md transition-all ${
              isEditMode 
                ? 'bg-zinc-900/60 text-zinc-100 border border-gold-500/40 border border-gold-500/40' 
                : 'bg-white/5 text-gray-500 hover:text-gray-300 hover:bg-white/10'
            }`}
          >
            {isEditMode ? '✓ Listo' : '⋮⋮ Ordenar / Ocultar'}
          </button>
          {isEditMode && (
            <button
              onClick={() => { resetOrder(); resetHiddenTools(); }}
              className="text-xs py-1.5 px-2 rounded-md bg-red-500/10 text-red-400 hover:bg-red-500/20 transition-colors"
            >
              Reset
            </button>
          )}
        </div>
      )}

      {/* Popup de preview de herramienta */}
      <ToolPreviewPopup
        toolId={popupToolId}
        isVisible={!!popupToolId}
        position={popupPosition}
        sectionColor={popupColor}
        fallback={menuSections.find((s) => s.id === popupToolId) || null}
        onMouseEnter={handlePopupMouseEnter}
        onMouseLeave={handlePopupMouseLeave}
        onSelect={(toolId) => {
          setPopupToolId(null); // Cerrar popup
          onSectionChange(toolId); // Abrir la herramienta
        }}
        onFeatureSelect={(toolId, feature, idx) => {
          setPopupToolId(null); // Cerrar popup
          // Abrir la herramienta con la feature seleccionada
          // Usar feature.action si existe, sino usar el texto como fallback
          onSectionChange(toolId, { 
            feature: feature.action || feature.text, 
            featureText: feature.text,
            featureIndex: idx 
          });
        }}
      />

      {/* Items del menú */}
      <nav 
        className="py-2 px-2 space-y-0.5 overflow-y-auto premium-scrollbar" 
        style={{ height: isCollapsed ? 'calc(100% - 60px)' : 'calc(100% - 100px)' }}
      >
        {(() => { const hiddenSet = new Set(hiddenTools); return menuSections.map((section, index) => {
          if (!isEditMode && hiddenSet.has(section.id)) return null;
          const Icon = section.icon;
          const isHovered = hoveredItem === section.id;
          const counter = section.hasCounter ? getCounter(section.id) : 0;
          const sectionColor = section.color || '#e8c55c';
          const isDragOver = dragOverItem === index && draggedItem !== index;
          
          // Verificar acceso según el plan del usuario
          // Múltiples fuentes: currentUser, localStorage directo, user_data
          let userPlan = currentUser?.plan;
          
          // Fallback a localStorage si no hay plan en currentUser
          if (!userPlan || userPlan === 'free') {
            // Intentar obtener de kapital_user_plan directo
            try {
              const directPlan = localStorage.getItem('kapital_user_plan');
              if (directPlan && directPlan !== 'free' && directPlan !== 'null' && directPlan !== 'undefined') {
                userPlan = directPlan;
              }
            } catch (e) {
              // Ignorar errores
            }
          }
          
          if (!userPlan || userPlan === 'free') {
            // Intentar obtener de user_data
            try {
              const userData = localStorage.getItem('user_data');
              if (userData && userData !== 'null' && userData !== 'undefined' && userData.startsWith('{')) {
                const parsed = JSON.parse(userData);
                if (parsed && parsed.plan && parsed.plan !== 'free') {
                  userPlan = parsed.plan;
                }
              }
            } catch (e) {
              // Ignorar errores de parsing - datos corruptos
            }
          }
          
          // Default a free si todo falla
          userPlan = userPlan || 'free';
          
          // Admin siempre tiene acceso total
          const isAdmin = currentUser?.is_admin || currentUser?.email === 'atorremartin73@gmail.com' || currentUser?.email === 'kapital.analisistecnico@gmail.com';
          const hasAccess = isAdmin || hasAccessToTool(section.id, userPlan);
          const requiredPlan = getRequiredPlan(section.id);
          const isLocked = !hasAccess;
          
          return (
            <div
              key={section.id}
              draggable={isEditMode}
              onDragStart={(e) => handleDragStart(e, index)}
              onDragEnd={handleDragEnd}
              onDragOver={(e) => handleDragOver(e, index)}
              onDrop={(e) => handleDrop(e, index)}
              className={`relative transition-all duration-200 ${isDragOver ? 'transform translate-y-1' : ''}`}
              style={{
                borderTop: isDragOver ? '2px solid #e8c55c' : '2px solid transparent',
                opacity: isEditMode && hiddenSet.has(section.id) ? 0.45 : 1,
              }}
            >
              <button
                onClick={(e) => {
                  if (isLocked) {
                    // Mostrar página de pricing si está bloqueado
                    onShowPricing();
                    return;
                  }
                  handleSectionClick(section.id, e);
                }}
                onMouseEnter={(e) => handleToolHover(section.id, sectionColor, e)}
                onMouseLeave={handleToolLeave}
                className={`w-full flex items-center gap-2.5 px-2.5 py-2.5 rounded-lg transition-all duration-200 group relative min-h-[44px] ${
                  isCollapsed ? 'justify-center' : ''
                } ${isEditMode ? 'cursor-grab active:cursor-grabbing' : 'cursor-pointer'} ${
                  isLocked ? 'opacity-60' : ''
                }`}
                style={{
                  background: isHovered ? `${sectionColor}15` : 'transparent',
                  border: isHovered ? `1px solid ${sectionColor}40` : '1px solid transparent',
                  boxShadow: isHovered ? `0 0 15px ${sectionColor}20` : 'none',
                }}
                data-testid={`sidebar-item-${section.id}`}
              >
                {/* Handle de arrastre en modo edición */}
                {isEditMode && !isCollapsed && (
                  <DotsSixVertical 
                    size={14} 
                    className="text-gray-600 group-hover:text-gold-400 transition-colors shrink-0"
                  />
                )}
                
                {/* Icono */}
                <div 
                  className="w-7 h-7 rounded-lg flex items-center justify-center shrink-0 transition-all relative"
                  style={{
                    background: `linear-gradient(135deg, ${sectionColor}25 0%, ${sectionColor}10 100%)`,
                    border: '1px solid rgba(232, 197, 92, 0.55)',
                    boxShadow: isHovered ? `0 0 12px rgba(232, 197, 92, 0.45)` : '0 0 6px rgba(232, 197, 92, 0.15)',
                  }}
                >
                  <Icon 
                    size={16} 
                    weight="fill"
                    style={{ 
                      color: sectionColor,
                      filter: isHovered ? `drop-shadow(0 0 6px ${sectionColor})` : 'none'
                    }}
                  />
                  {/* Badge contador */}
                  {counter > 0 && (
                    <PulseBadge 
                      count={counter} 
                      size="sm"
                      className="absolute -top-1 -right-1"
                    />
                  )}
                  {/* Badge de candado para herramientas bloqueadas */}
                  {isLocked && (
                    <div className="absolute -top-1 -right-1 w-4 h-4 bg-gold-500 rounded-full flex items-center justify-center text-[10px]">
                      🔒
                    </div>
                  )}
                </div>

                {/* Label */}
                {!isCollapsed && (
                  <div className="flex-1 text-left min-w-0 relative">
                    <div className="flex items-center gap-1.5">
                      <span 
                        className="text-sm font-semibold truncate transition-colors"
                        style={{ 
                          color: isHovered ? sectionColor : 'rgba(255,255,255,0.85)',
                          textShadow: isHovered ? `0 0 10px ${sectionColor}50` : 'none'
                        }}
                        title={section.label}
                      >
                        {section.label}
                      </span>
                      {/* Badge de plan requerido */}
                      {isLocked && (() => {
                        // Normalizar: basic == pro (nivel 1), premium == ultimate (nivel 2)
                        const isUltimateOnly = requiredPlan === 'premium' || requiredPlan === 'ultimate';
                        return (
                          <span className={`px-1.5 py-0.5 rounded text-[9px] font-bold border ${
                            isUltimateOnly
                              ? 'bg-gold-500/15 text-gold-300 border-gold-500/40'
                              : 'bg-gold-500/15 text-gold-300 border-gold-500/40'
                          }`}>
                            {isUltimateOnly ? 'ULTIMATE' : 'PRO'}
                          </span>
                        );
                      })()}
                    </div>
                    <div 
                      className="text-[11px] text-gray-500 truncate"
                      title={isLocked ? `Requiere plan ${(requiredPlan === 'premium' || requiredPlan === 'ultimate') ? 'Ultimate' : 'Pro'}` : section.description}
                    >
                      {isLocked ? `Requiere plan ${(requiredPlan === 'premium' || requiredPlan === 'ultimate') ? 'Ultimate' : 'Pro'}` : section.description}
                    </div>
                    
                    {/* Tooltip expandido con preview al hacer hover (solo en modo expandido) */}
                    {isHovered && section.preview && (
                      <div 
                        className="absolute left-full top-0 ml-2 px-3 py-2 rounded-lg z-50 min-w-[200px] max-w-[280px] animate-in fade-in slide-in-from-left-2 duration-200"
                        style={{
                          background: 'rgba(10, 25, 45, 0.98)',
                          border: `1px solid ${sectionColor}50`,
                          boxShadow: `0 4px 20px rgba(0,0,0,0.5), 0 0 15px ${sectionColor}20`
                        }}
                      >
                        <div className="text-xs font-semibold mb-1" style={{ color: sectionColor }}>
                          ¿Qué incluye?
                        </div>
                        <div className="text-[10px] text-gray-300 leading-relaxed">
                          {section.preview}
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* Tooltip para modo colapsado - con preview */}
                {isCollapsed && (
                  <div 
                    className="absolute left-full ml-3 px-3 py-2 rounded-lg opacity-0 pointer-events-none group-hover:opacity-100 group-hover:pointer-events-auto transition-opacity z-50 min-w-[200px] max-w-[280px]"
                    style={{
                      background: 'rgba(10, 25, 45, 0.98)',
                      border: `1px solid ${sectionColor}50`,
                      boxShadow: `0 4px 20px rgba(0,0,0,0.5), 0 0 15px ${sectionColor}20`
                    }}
                  >
                    <div className="text-xs font-semibold" style={{ color: sectionColor }}>
                      {section.label}
                    </div>
                    <div className="text-[10px] text-gray-400 mb-1">{section.description}</div>
                    {section.preview && (
                      <>
                        <div className="border-t border-gray-700 my-1.5"></div>
                        <div className="text-[10px] text-gray-300 leading-relaxed">
                          {section.preview}
                        </div>
                      </>
                    )}
                  </div>
                )}
              </button>
              {/* Toggle mostrar/ocultar (modo edición) */}
              {isEditMode && !isCollapsed && (
                <button
                  onClick={(e) => { e.stopPropagation(); toggleToolHidden(section.id); }}
                  className="absolute top-1/2 -translate-y-1/2 right-2 w-7 h-7 rounded-full flex items-center justify-center transition-all z-10"
                  style={{ background: hiddenSet.has(section.id) ? 'rgba(120,120,120,0.3)' : `${sectionColor}22`, border: `1px solid ${hiddenSet.has(section.id) ? 'rgba(160,160,160,0.4)' : sectionColor + '66'}` }}
                  title={hiddenSet.has(section.id) ? 'Mostrar en el menú' : 'Ocultar del menú'}
                  data-testid={`sidebar-visibility-${section.id}`}
                >
                  {hiddenSet.has(section.id) ? <EyeSlash size={14} weight="bold" style={{ color: '#cbd5e1' }} /> : <Eye size={14} weight="bold" style={{ color: sectionColor }} />}
                </button>
              )}
            </div>
          );
        }); })()}
        
        {/* Botón de Atajos de Teclado - Dentro del scroll */}
        <div className="mt-2 pt-2" style={{ borderTop: '1px solid rgba(232, 197, 92, 0.12)' }}>
          {!isCollapsed ? (
            <div className="space-y-1">
              {/* Botón de Historial de Exportaciones */}
              <button
                onClick={openExportHistory}
                className="w-full flex items-center gap-2.5 px-2.5 py-2 rounded-lg transition-all duration-200 hover:bg-gold-500/10 group"
                style={{ border: '1px solid transparent' }}
                data-testid="export-history-button"
              >
                <div 
                  className="w-7 h-7 rounded-lg flex items-center justify-center shrink-0"
                  style={{
                    background: 'linear-gradient(135deg, rgba(236, 72, 153, 0.25) 0%, rgba(236, 72, 153, 0.1) 100%)',
                    border: '1px solid rgba(236, 72, 153, 0.35)'
                  }}
                >
                  <ClockCounterClockwise size={16} className="text-pink-400" style={{ filter: 'drop-shadow(0 0 4px rgba(236, 72, 153, 0.5))' }} />
                </div>
                <div className="flex-1 text-left min-w-0">
                  <div className="text-xs font-semibold text-white/85 group-hover:text-pink-300 transition-colors">
                    Historial Exportaciones
                  </div>
                  <div className="text-[10px] text-gray-500">
                    PDF e Impresiones
                  </div>
                </div>
              </button>
              
              {/* Botón de Atajos de Teclado */}
              <button
                onClick={onShowKeyboardShortcuts}
                className="w-full flex items-center gap-2.5 px-2.5 py-2 rounded-lg transition-all duration-200 hover:bg-gold-500/10 group"
                style={{ border: '1px solid transparent' }}
                data-testid="keyboard-shortcuts-button"
              >
                <div 
                  className="w-7 h-7 rounded-lg flex items-center justify-center shrink-0"
                  style={{
                    background: 'linear-gradient(135deg, rgba(168, 85, 247, 0.25) 0%, rgba(168, 85, 247, 0.1) 100%)',
                    border: '1px solid rgba(168, 85, 247, 0.35)'
                  }}
                >
                  <Keyboard size={16} className="text-gold-400" style={{ filter: 'drop-shadow(0 0 4px rgba(168, 85, 247, 0.5))' }} />
                </div>
                <div className="flex-1 text-left min-w-0">
                  <div className="text-xs font-semibold text-white/85 group-hover:text-gold-300 transition-colors">
                    Atajos de Teclado
                  </div>
                  <div className="text-[10px] text-gray-500">
                    Presiona <kbd className="px-1 py-0.5 bg-white/10 rounded text-[9px] font-mono">?</kbd> para ver
                  </div>
                </div>
              </button>
            </div>
          ) : (
            <div className="flex flex-col items-center gap-1">
              <button
                onClick={openExportHistory}
                className="p-2 rounded-lg transition-all duration-200 hover:bg-pink-500/10 group"
                title="Historial de Exportaciones"
                data-testid="export-history-button-collapsed"
              >
                <ClockCounterClockwise size={18} className="text-pink-400 group-hover:text-pink-300" />
              </button>
              <button
                onClick={onShowKeyboardShortcuts}
                className="p-2 rounded-lg transition-all duration-200 hover:bg-gold-500/10 group"
                title="Atajos de Teclado (?)"
                data-testid="keyboard-shortcuts-button-collapsed"
              >
                <Keyboard size={18} className="text-gold-400 group-hover:text-gold-300" />
              </button>
            </div>
          )}
        </div>
      </nav>

      {/* Modal de Historial de Exportaciones */}
      <ExportHistoryPanel 
        isOpen={showExportHistory}
        onClose={closeExportHistory}
        isExternalMinimized={historyMinimized}
        onMinimizedChange={setHistoryMinimized}
      />

      {/* Footer con contacto */}
      {!isCollapsed && (
        <div 
          className="absolute bottom-0 left-0 right-0 p-2 text-center border-t"
          style={{ borderColor: 'rgba(232, 197, 92, 0.12)', background: 'rgba(0, 0, 0, 0.3)' }}
        >
          <a 
            href="mailto:kapital.analisistecnico@gmail.com" 
            className="kap-link text-[10px] text-gray-500 hover:text-gold-400 transition-colors flex items-center justify-center gap-1"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
            </svg>
            Contacto
          </a>
        </div>
      )}

      {/* Indicador de modo edición */}
      {isEditMode && !isCollapsed && (
        <div 
          className="absolute bottom-0 left-0 right-0 p-3 text-center text-xs"
          style={{
            background: 'linear-gradient(to top, rgba(201, 162, 39, 0.1), transparent)',
            color: '#e8c55c'
          }}
        >
          Arrastra para reordenar
        </div>
      )}
    </div>
  );
};

export default Sidebar3D;
